import { html, css, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import '@awesome.me/webawesome/dist/components/button/button.js';

import NSAutoPaymentsCard from './card-base.js';
import JobSchema from '../../../../../models/job-schema.js';
import type Company from '../../../../../models/company.js';
import type Workflow from '../../../../../models/workflow.js';
import definition from '@job-schemas/public-ledger/auto-payments/change-amounts.json';
import { jobSchema } from '../../../job-schema.js';
import { AUTOPAY_CARD_OUTCOMES } from './presentations.js';
import { nsFormFieldStyles } from '../../../../../ns-styles/index.js';
import { createLogger } from '../../../../../ns-services/logger.js';
import { lambdaErrorCode } from '../../../../../helpers/lambda.js';
import { lambdaRefusal } from '../../../../../helpers/public-ledger-balance-api.js';
import { formatCredits } from '../../../../../helpers/public-ledger-credits.js';
import { formatUnitPence } from '../../../../../helpers/credit-pricing.js';
import { AUTOPAY_CODES, RATE_NOTE, type SetupFigures } from '../../../../../helpers/auto-payments.js';
import { autopayFigures, proposeAutopayChange } from '../../../../../helpers/auto-payments-api.js';
import {
  AMOUNTS_ALREADY_OPEN_TEXT,
  CHANGE_AMOUNTS_BEFORE,
  CHANGE_AMOUNTS_BUTTON,
  CHANGE_AMOUNTS_INTRO,
  CHANGE_SIGNING_TEXT,
  NOTHING_CHANGED_TEXT,
  SIGNATORY_CHANGE_REASON,
  changeAmountsContext,
  changeAmountsStage,
  changeDoneText,
  draftFromRow,
  focusFields,
  raiseTargetDraft,
  sameAmounts,
  type ChangeAmountsStage,
} from '../../../../../helpers/auto-payment-changes.js';
import { AutopaySettingsForm } from './settings-form.js';

import '../../../../../ns-elements/ns-job-layout.js';
import '../../../../../ns-elements/ns-detail-list.js';

const logger = createLogger('ns-public-ledger-auto-payments-change-amounts');

/**
 * Change automatic payments amounts (#25 phase 6): held by the signatory who started it, from My Account or from a Limit
 * reached (Raise), Change target or Raise operating target (Raise) card. The form starts from the company's live amounts,
 * with the ones the card was about in focus; from Raise operating target the operating target starts at the suggested one
 * (never lower than the live one), and the form's own checks say when the £ target must rise too. Complete sends them to
 * public-ledger/autopay/propose-change, which checks them again at today's price, records them and completes the job
 * `proposed`; the workflow then schedules the approval (made and paid by the company's operating account) for every
 * signatory to sign, and the amounts apply once it runs. The card just closes when the job's update arrives. With nothing
 * changed, Complete says so first; pressed again it completes the job `unchanged` (no proposal, nothing to sign). Cancel
 * ends the change. Once closed, the card says whether the change waits for signatures or how it ended.
 */
@customElement(NSPublicLedgerAutoPaymentsChangeAmounts.is)
export default class NSPublicLedgerAutoPaymentsChangeAmounts extends NSAutoPaymentsCard {
  static get is() {
    return 'ns-public-ledger-auto-payments-change-amounts';
  }

  static get schema(): JobSchema {
    // Every outcome is hidden: presentations.ts says whose each one is
    return jobSchema(definition, {
      outcomes: AUTOPAY_CARD_OUTCOMES['@ns/public-ledger-auto-payments-change-amounts'],
      icon: 'bank-card',
    });
  }

  static styles = [
    nsFormFieldStyles,
    ...super.styles,
    css`
      .job-panel ns-detail-list {
        --ns-detail-list-gap: var(--wa-space-2xs);
      }

      .fields {
        display: grid;
        gap: var(--wa-space-m);
      }

      .field input {
        max-width: 16rem;
      }

      wa-radio-group::part(form-control-label) {
        font-size: var(--wa-font-size-m);
        font-weight: 600;
      }
    `,
  ];

  @state()
  private __figures: SetupFigures | null = null;

  // Whether a signatory change is pending: propose-change refuses then
  @state()
  private __changePending = false;

  // The transaction propose-change answered, so the card knows its own proposal before the row's update arrives
  @state()
  private __proposedTransactionId: string | null = null;

  // Whether the form has been started from the live amounts
  private __started = false;

  // The amounts a first press of Complete found unchanged (and said so): a second press with the same closes the job
  private __nothingChanged: string | null = null;

  // The change's workflow, once the job is done: its outcome says how the change ended
  @state()
  private __workflow: (Workflow & { outcome?: string | null }) | null = null;

  private __form = new AutopaySettingsForm(this, { figures: () => this.__figures, busy: () => this._busy });

  async connectedCallback() {
    await super.connectedCallback();
    const job = this._job;
    if (!job) return;
    await this.__loadCompany();
    await this.__loadWorkflow();
    const workflowId = job.workflow?.id;
    if (workflowId) this._subscriptions.subscribe(`workflow.${workflowId}.*`, () => void this.__loadWorkflow());
    if (!this._completed && this._claimed) await this.__loadFigures();
    this.__startForm();
  }

  // Read once the job is done, and again on the workflow's updates
  private async __loadWorkflow() {
    const workflowId = this._job?.workflow?.id;
    if (!workflowId || !this._db || !this._completed) return;
    try {
      this.__workflow =
        ((await this._db.getById('workflow', workflowId)) as typeof this.__workflow | undefined) ?? null;
    } catch (err) {
      logger.warn('Unable to read the change', err);
    }
  }

  protected async _loadRow() {
    await super._loadRow();
    this.__startForm();
  }

  private get __context() {
    return changeAmountsContext(this._job?.metadata);
  }

  private get __stage(): ChangeAmountsStage {
    return changeAmountsStage({
      completed: this._completed,
      claimed: this._claimed,
      row: this._row,
      personId: this._me,
      proposedTransactionId: this.__proposedTransactionId,
      changePending: this.__changePending,
      rowLoaded: this._rowLoaded,
    });
  }

  // Starts the form from the live amounts once they are read, with the fields the card was about in focus
  private __startForm() {
    if (this.__started || this._row?.setupState !== 'ACTIVE') return;
    this.__started = true;
    const context = this.__context;
    this.__form.start(raiseTargetDraft(draftFromRow(this._row), context.suggestedOperatingTargetCredits));
    void this.__form.focus(focusFields(context.focus, context.limit));
  }

  // The second press of Complete with nothing changed: the job closes with no proposal and nothing to sign
  private async __completeUnchanged() {
    this._busy = true;
    try {
      await this.outcome('unchanged');
    } catch (err) {
      // Approved (round 4 follow-ups, small string 2)
      this.showError(lambdaRefusal(err, 'This job could not be completed. Please try again.'));
    } finally {
      this._busy = false;
    }
  }

  private async __loadCompany() {
    const companyId = this._job?.companyId;
    if (!companyId || !this._db) return;
    try {
      const company = (await this._db.getById('company', companyId)) as Company | undefined;
      this.__changePending = company?.ledgerCred?.signatureRequirement?.awaitingUpdate === true;
    } catch (err) {
      logger.warn('Unable to read the company', err);
    }
  }

  private async __loadFigures() {
    const companyId = this._job?.companyId;
    if (!companyId || !this._auth?.token) return;
    try {
      this.__figures = await autopayFigures(this._auth.token, companyId);
    } catch (err) {
      this.showError(lambdaRefusal(err, "Today's price can't be read. Please try again."));
    }
  }

  protected async _claim() {
    await super._claim();
    if (this._claimed) await this.__loadFigures();
  }

  /**
   * Complete: proposes the new amounts; the card then just closes (its job completes a moment later). With nothing
   * changed, the first press says so and a second press, still with nothing changed, closes the job with no proposal and
   * nothing to sign.
   */
  private async __complete() {
    const job = this._job;
    if (!job || !this._auth?.token || this._busy) return;
    this.clearError();
    const amounts = this.__form.submit();
    if (!amounts) {
      this.__nothingChanged = null;
      return;
    }
    if (sameAmounts(amounts, this._row)) {
      const shown = JSON.stringify(amounts);
      if (this.__nothingChanged === shown) {
        await this.__completeUnchanged();
        return;
      }
      this.__nothingChanged = shown;
      this.showError(NOTHING_CHANGED_TEXT);
      return;
    }
    this.__nothingChanged = null;

    this._busy = true;
    this.loading('Proposing the new amounts');
    try {
      const { transactionId } = await proposeAutopayChange(this._auth.token, { jobId: job.id, settings: amounts });
      this.__proposedTransactionId = transactionId;
      // No closing line: the card closes once the server has completed its job
      this._closeWhenComplete = true;
      const updated = await this._db?.getById('job', job.id);
      if (updated) this._jobUpdated(updated as unknown as typeof job);
    } catch (err) {
      const code = lambdaErrorCode(err);
      const message = lambdaRefusal(err, 'The new amounts could not be proposed. Please try again.');
      if (code === AUTOPAY_CODES.settingsInvalid) {
        if (!this.__form.showServerProblems(message)) this.showError(message);
        // The server checked at its own price: refresh it so the conversions match
        await this.__loadFigures();
      } else {
        this.showError(message);
        if (code === AUTOPAY_CODES.noRate) await this.__loadFigures();
        if (code === AUTOPAY_CODES.signatoryChange) await this.__loadCompany();
      }
    } finally {
      this.loaded();
      this._busy = false;
    }
  }

  private __renderStage(): TemplateResult | typeof nothing {
    switch (this.__stage) {
      case 'DONE': {
        // No line while the change's workflow can't be read, or when it ended unchanged: the card just closes
        const done = changeDoneText('AMOUNTS', this.__workflow);
        return done
          ? html`
              <div class="job-panel">
                <p>${done}</p>
              </div>
            `
          : nothing;
      }
      case 'CLAIM':
        return this._renderClaimNote('change the amounts');
      case 'LOADING':
        return nothing;
      case 'PROPOSED':
        return html`
          <div class="job-panel" role="status">
            <p>${CHANGE_SIGNING_TEXT.AMOUNTS}</p>
          </div>
        `;
      case 'CHANGE_OPEN':
        return html`
          <div class="job-panel">
            <p>${AMOUNTS_ALREADY_OPEN_TEXT}</p>
          </div>
        `;
      case 'LOCKED':
        return html`
          <div class="job-panel">
            <p>${SIGNATORY_CHANGE_REASON}.</p>
          </div>
          ${this.__form.render()}
        `;
      default:
        return html`
          ${this.__form.render()}
          <div class="job-panel">
            <p>${CHANGE_AMOUNTS_BEFORE}</p>
          </div>
        `;
    }
  }

  private __renderPrice() {
    const unit = this.__figures?.unitPricePence ?? null;
    return html`
      <div class="primary-info" slot="aside">
        <span class="info-icon" aria-hidden="true">i</span>
        <h3>Today's price</h3>
        <ns-detail-list
          .rows=${[
            { label: 'Price per credit', value: unit !== null ? formatUnitPence(unit) : "Can't be read right now" },
            {
              label: 'Smallest purchase',
              value: this.__figures ? `${formatCredits(this.__figures.minCredits)} credits` : undefined,
            },
          ]}
        ></ns-detail-list>
        <p class="note">${RATE_NOTE}</p>
      </div>
    `;
  }

  render() {
    const stage = this.__stage;
    const working = stage === 'FORM' || stage === 'LOCKED';
    return html`
      <ns-job-layout>
        <p slot="intro">${CHANGE_AMOUNTS_INTRO}</p>
        ${this._renderWaiting() ?? this.__renderStage()} ${working ? this.__renderPrice() : nothing}
      </ns-job-layout>
    `;
  }

  renderActions() {
    if (this._completed || this._waiting !== null) return nothing;
    if (!this._claimed) return this._renderClaimButton();
    const stage = this.__stage;
    if (stage === 'LOADING' || stage === 'PROPOSED') return nothing;
    const cancel = html`
      <wa-button
        variant="brand"
        appearance="outlined"
        type="button"
        ?disabled=${this._busy}
        @click=${() => this.outcome('cancel')}
      >
        Cancel
      </wa-button>
    `;
    if (stage !== 'FORM' && stage !== 'LOCKED') return cancel;
    return html`
      ${cancel}
      <wa-button
        variant="brand"
        type="button"
        ?disabled=${this._busy || stage === 'LOCKED' || !this.__figures?.unitPricePence}
        @click=${() => this.__complete()}
      >
        ${CHANGE_AMOUNTS_BUTTON}
      </wa-button>
    `;
  }
}
